import { useEffect, useState } from "react";
import { useOrganization } from "../../App";
import {
  WEBHOOK_EVENT_OPTIONS,
  Webhook,
  WebhookInput,
  webhookService,
} from "../../services/webhookService";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Copy, Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";

const EMPTY_INPUT: WebhookInput = { url: "", description: "", events: [], isActive: true };

export default function WebhooksTab() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [webhookInput, setWebhookInput] = useState<WebhookInput>(EMPTY_INPUT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shownSecrets, setShownSecrets] = useState<Set<string>>(new Set());

  const { currentOrganization } = useOrganization();
  const { toast } = useToast();

  useEffect(() => {
    if (currentOrganization) {
      fetchWebhooks();
    }
  }, [currentOrganization]);

  const fetchWebhooks = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const result = await webhookService.getWebhooks(currentOrganization.id);
      setWebhooks(result.webhooks);
      setCanManage(result.canManage);
    } catch (error) {
      console.error("Error fetching webhooks:", error);
      toast({
        title: "Error",
        description: "Failed to load webhooks. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingWebhook(null);
    setWebhookInput(EMPTY_INPUT);
    setDialogOpen(true);
  };

  const openEditDialog = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setWebhookInput({
      url: webhook.url,
      description: webhook.description || "",
      events: webhook.events || [],
      isActive: webhook.is_active !== false,
    });
    setDialogOpen(true);
  };

  const toggleEvent = (eventId: string) => {
    setWebhookInput((prev) => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter((id) => id !== eventId)
        : [...prev.events, eventId],
    }));
  };

  const handleSubmit = async () => {
    if (!currentOrganization) return;
    if (!webhookInput.url.trim()) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid webhook URL.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingWebhook) {
        await webhookService.updateWebhook(currentOrganization.id, editingWebhook.id, webhookInput);
        toast({
          title: "Webhook Updated",
          description: "The webhook has been updated successfully.",
        });
      } else {
        await webhookService.createWebhook(currentOrganization.id, webhookInput);
        toast({
          title: "Webhook Created",
          description: "The webhook has been created. Copy its signing secret from the list.",
        });
      }

      setDialogOpen(false);
      fetchWebhooks();
    } catch (error: any) {
      console.error("Error saving webhook:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save webhook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (webhook: Webhook) => {
    if (!currentOrganization) return;

    if (!confirm("Are you sure you want to delete this webhook?")) {
      return;
    }

    try {
      await webhookService.deleteWebhook(currentOrganization.id, webhook.id);
      toast({
        title: "Webhook Deleted",
        description: "The webhook has been deleted successfully.",
      });
      fetchWebhooks();
    } catch (error: any) {
      console.error("Error deleting webhook:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete webhook. Please try again.",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string, what: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${what} copied to clipboard.`,
    });
  };

  const toggleSecret = (id: string) => {
    setShownSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Call Webhooks</CardTitle>
          <CardDescription>
            Receive your organization's call events as they happen. Each event is a POST with a
            JSON body to every active webhook below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium">Events</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                {WEBHOOK_EVENT_OPTIONS.map((event) => (
                  <span
                    key={event.id}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                    title={event.id}
                  >
                    {event.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              Every request carries <code className="font-mono">X-Voxi-Event</code>,{" "}
              <code className="font-mono">X-Voxi-Timestamp</code> and{" "}
              <code className="font-mono">X-Voxi-Signature: v1=&lt;hex&gt;</code>, the HMAC-SHA256
              of <code className="font-mono">&lt;timestamp&gt;.&lt;body&gt;</code> with the
              webhook's signing secret. Check it to be sure the request came from Voxi.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Webhooks</CardTitle>
              <CardDescription>
                {canManage
                  ? "Webhooks configured for your organization."
                  : "Webhooks configured for your organization. Owners and admins can change them."}
              </CardDescription>
            </div>
            {canManage && (
              <Button onClick={openCreateDialog} variant="default">
                <Plus className="mr-2 h-4 w-4" />
                Add Webhook
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-border rounded-none">
              <h3 className="text-lg font-medium">No webhooks configured</h3>
              <p className="text-muted-foreground max-w-md mx-auto mt-2">
                Add a webhook to receive call events (initiated, ringing, answered, completed) at
                your own URL.
              </p>
              {canManage && (
                <Button onClick={openCreateDialog} variant="outline" className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  Create First Webhook
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-none border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Events</TableHead>
                    {canManage && <TableHead>Signing secret</TableHead>}
                    {canManage && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell className="font-mono truncate max-w-[200px]" title={webhook.url}>
                        {webhook.url}
                      </TableCell>
                      <TableCell>
                        {webhook.description || (
                          <span className="text-muted-foreground italic">No description</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-none text-xs ${
                            webhook.is_active !== false
                              ? "bg-good-bg text-good"
                              : "bg-mid-bg text-mid"
                          }`}
                        >
                          {webhook.is_active !== false ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(webhook.events || []).map((eventId) => {
                            const event = WEBHOOK_EVENT_OPTIONS.find((e) => e.id === eventId);
                            return (
                              <span
                                key={eventId}
                                className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                              >
                                {event?.label || eventId}
                              </span>
                            );
                          })}
                          {(!webhook.events || webhook.events.length === 0) && (
                            <span className="text-muted-foreground italic text-xs">All events</span>
                          )}
                        </div>
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          {webhook.secret && (
                            <div className="flex items-center gap-1">
                              <code className="font-mono text-xs">
                                {shownSecrets.has(webhook.id)
                                  ? webhook.secret
                                  : `${webhook.secret.slice(0, 10)}••••••`}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleSecret(webhook.id)}
                                title={shownSecrets.has(webhook.id) ? "Hide" : "Show"}
                              >
                                {shownSecrets.has(webhook.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(webhook.secret!, "Signing secret")}
                                title="Copy"
                              >
                                <Copy size={14} />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                      {canManage && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => openEditDialog(webhook)}
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDelete(webhook)}
                              title="Delete"
                            >
                              <Trash2 size={16} className="text-danger" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>{editingWebhook ? "Edit Webhook" : "Create Webhook"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="url">Webhook URL *</Label>
              <Input
                id="url"
                value={webhookInput.url}
                onChange={(e) => setWebhookInput({ ...webhookInput, url: e.target.value })}
                placeholder="https://example.com/webhook"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={webhookInput.description || ""}
                onChange={(e) => setWebhookInput({ ...webhookInput, description: e.target.value })}
                placeholder="What is this webhook for?"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={webhookInput.isActive}
                onCheckedChange={(checked) => setWebhookInput({ ...webhookInput, isActive: checked })}
              />
              <Label htmlFor="isActive">Webhook is active</Label>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Event Filters</Label>
              <div className="text-sm text-muted-foreground mb-2">
                Select which events should trigger this webhook. If none are selected, all events
                will be sent.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-none p-3">
                {WEBHOOK_EVENT_OPTIONS.map((event) => (
                  <div key={event.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`event-${event.id}`}
                      checked={webhookInput.events.includes(event.id)}
                      onChange={() => toggleEvent(event.id)}
                      className="h-4 w-4 rounded-none border-border text-primary focus:ring-primary"
                    />
                    <label
                      htmlFor={`event-${event.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {event.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  {editingWebhook ? "Updating..." : "Creating..."}
                </>
              ) : editingWebhook ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
